import { useEffect, useReducer } from 'react';
import { refetchSessionQuietly } from './sessionRefetch';

/** At most one session refetch per tab in this window. */
export const SESSION_RECHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Re-renders the caller when the tab is shown again, so state read during
 * render (cookies) is re-checked as the session refetch used to make it.
 * While signed in it also refetches the session, at most once per
 * SESSION_RECHECK_INTERVAL_MS, so a session cookie cleared by middleware
 * signs this tab out.
 */
export const useRecheckOnTabReturn = (
  enabled: boolean,
  isSignedIn = false
): void => {
  const [, recheck] = useReducer((revision: number) => revision + 1, 0);
  useEffect(() => {
    if (!enabled) return;
    let lastSessionCheck = Date.now();
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      recheck();
      if (
        isSignedIn &&
        Date.now() - lastSessionCheck >= SESSION_RECHECK_INTERVAL_MS
      ) {
        lastSessionCheck = Date.now();
        refetchSessionQuietly();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, isSignedIn]);
};
